import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Copy, Mail, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, SectionTag } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOOKING_FOR = ["Corporate Videos", "Testimonial Videos", "Product Shoot", "Social Media Videos", "AI Videos", "Others"];
const BUDGETS = ["Under 10K", "10K - 25K", "25K - 50K", "50K - 1L", "1L - 2L", "2L - 3L", "3L - 5L", "5L - 10L", "10L - 20L", "20L+"];

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company: "",
  designation: "",
  service: "",
  budget: "",
  brief: "",
};

const fieldClass =
  "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-crimson/60 focus:ring-2 focus:ring-crimson/15 transition-all duration-300";

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(", ");
  if (typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
};

export default function BriefForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const next = {};
    if (!form.first_name.trim()) next.first_name = "First name is required";
    if (!form.last_name.trim()) next.last_name = "Last name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10) next.phone = "Enter a valid 10-digit number";
    if (!form.service) next.service = "Select what you're looking for";
    if (!form.budget) next.budget = "Select a budget range";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/track/${done.track_code}`);
      toast.success("Tracking link copied");
    } catch {
      toast.error("Could not copy — select the link manually.");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/submissions`, { ...form, phone: form.phone.replace(/\D/g, "") });
      toast.success("Brief received! Our producers will reach out within 24 hours.", {
        description: `${form.first_name} ${form.last_name} · ${form.budget}`,
      });
      setForm(EMPTY);
      setDone(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="brief" className="py-24 md:py-32 bg-[#F7F7F8] border-t border-gray-100 scroll-mt-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_20%_30%,rgba(255,42,59,0.05),transparent_60%)] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <SectionTag>Start a Project</SectionTag>
          <h2 className="font-display font-extrabold text-gray-900 text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]">
            READY TO ELEVATE
            <br />
            YOUR <span className="text-crimson">CONTENT?</span>
          </h2>
          <p className="text-gray-500 text-base leading-relaxed mt-6 max-w-md">
            Fill in your requirement and a producer gets back to you within 24 hours with final details,
            pricing and your quote.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <a
              href="mailto:business.vrmedia@gmail.com"
              data-testid="brief-contact-email"
              className="flex items-center gap-3 text-gray-700 hover:text-crimson transition-colors duration-300"
            >
              <Mail className="w-4 h-4 text-crimson" /> business.vrmedia@gmail.com
            </a>
            <div className="flex items-center gap-3 text-gray-700">
              <MapPin className="w-4 h-4 text-crimson" /> India · Serving creators worldwide
            </div>
          </div>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="requirement-form"
            className="rounded-3xl border border-gray-100 bg-white shadow-soft p-6 md:p-10 grid sm:grid-cols-2 gap-5"
          >
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">First Name *</label>
              <input
                data-testid="input-first-name"
                className={fieldClass}
                placeholder="First name"
                value={form.first_name}
                onChange={set("first_name")}
              />
              {errors.first_name && <p className="text-crimson text-xs mt-1.5">{errors.first_name}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Last Name *</label>
              <input
                data-testid="input-last-name"
                className={fieldClass}
                placeholder="Last name"
                value={form.last_name}
                onChange={set("last_name")}
              />
              {errors.last_name && <p className="text-crimson text-xs mt-1.5">{errors.last_name}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-2">Email *</label>
              <input
                data-testid="input-email"
                type="email"
                className={fieldClass}
                placeholder="you@company.com"
                value={form.email}
                onChange={set("email")}
              />
              {errors.email && <p className="text-crimson text-xs mt-1.5">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Phone Number *</label>
              <div className="flex gap-2">
                <span className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-sm text-gray-700 font-mono">+91</span>
                <input
                  data-testid="input-phone"
                  inputMode="numeric"
                  className={fieldClass}
                  placeholder="98765 43210"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </div>
              {errors.phone && <p className="text-crimson text-xs mt-1.5">{errors.phone}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Company / Channel Name</label>
              <input
                data-testid="input-company"
                className={fieldClass}
                placeholder="Your company or channel"
                value={form.company}
                onChange={set("company")}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Designation</label>
              <input
                data-testid="input-designation"
                className={fieldClass}
                placeholder="Founder, Creator, Producer…"
                value={form.designation}
                onChange={set("designation")}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">What are you looking for? *</label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="select-service" className="w-full rounded-xl border-gray-200 bg-gray-50 px-4 py-3.5 h-auto text-sm text-gray-900 focus:ring-crimson/15 data-[placeholder]:text-gray-400">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-100 text-gray-900">
                  {LOOKING_FOR.map((o) => (
                    <SelectItem key={o} value={o} data-testid={`select-service-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.service && <p className="text-crimson text-xs mt-1.5">{errors.service}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-2">Budget (INR) *</label>
              <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                <SelectTrigger data-testid="select-budget" className="w-full rounded-xl border-gray-200 bg-gray-50 px-4 py-3.5 h-auto text-sm text-gray-900 focus:ring-crimson/15 data-[placeholder]:text-gray-400">
                  <SelectValue placeholder="Select a budget range" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-100 text-gray-900">
                  {BUDGETS.map((o) => (
                    <SelectItem key={o} value={o} data-testid={`select-budget-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.budget && <p className="text-crimson text-xs mt-1.5">{errors.budget}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-2">Project Brief (optional)</label>
              <textarea
                data-testid="textarea-brief"
                rows={4}
                className={`${fieldClass} resize-none`}
                placeholder="Tell us about the videos you need — format, volume, references…"
                value={form.brief}
                onChange={set("brief")}
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="submit-requirement-form"
                disabled={submitting}
                className="btn-glow w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full bg-crimson text-white font-bold text-sm tracking-wide disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Submit Requirement"}
                {!submitting && <ArrowUpRight className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </Reveal>
      </div>

      {done && (
        <div className="relative max-w-7xl mx-auto px-6 mt-10">
          <div
            className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 md:p-7 space-y-4"
            data-testid="form-success-panel"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
              <p className="text-sm text-emerald-700 leading-relaxed">
                Your brief is in. Our producers will contact you within 24 hours — and we&apos;ve emailed you a copy.
              </p>
            </div>
            <div className="rounded-xl border border-emerald-200/60 bg-white p-4">
              <div className="font-mono text-[10px] tracking-[0.25em] text-gray-400 uppercase">Your Tracking Code</div>
              <div className="font-mono text-lg font-bold text-gray-900 mt-1" data-testid="form-track-code">
                {done.track_code}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <a
                  href={`/track/${done.track_code}`}
                  data-testid="form-track-link"
                  className="text-xs text-gray-600 underline underline-offset-4 hover:text-crimson transition-colors truncate"
                >
                  {`${window.location.origin}/track/${done.track_code}`.replace(/^https?:\/\//, "")}
                </a>
              </div>
              <button
                type="button"
                data-testid="copy-track-link-button"
                onClick={copyLink}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 text-xs font-semibold text-gray-700 hover:border-crimson hover:text-crimson transition-colors duration-300"
              >
                <Copy className="w-3.5 h-3.5" /> Copy tracking link
              </button>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              Save this link — you can check your brief status anytime, no login needed.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
